import { useCallback, useState } from 'react';
import type { OnChangeFn, Row, SortingState } from '@tanstack/react-table';

const NO_IDS: readonly string[] = [];

/**
 * The sort, and the rows added from the blank row in the order they were
 * added. Those stay where they were typed rather than jumping to their sorted
 * place, as in a spreadsheet, until somebody sorts again.
 */
export function useRowOrder(initialSorting: SortingState) {
  const [sorting, setSorting] = useState(initialSorting);
  const [appendedIds, setAppendedIds] = useState(NO_IDS);
  const onSortingChange = useCallback<OnChangeFn<SortingState>>(update => {
    setSorting(update);
    setAppendedIds(NO_IDS);
  }, []);
  const append = useCallback(
    (id: string) => setAppendedIds(prev => [...prev, id]),
    []
  );
  return { sorting, onSortingChange, appendedIds, append };
}

/** The sorted rows, with the added ones moved to the bottom in order added. */
export function orderRows<T>(
  sorted: Row<T>[],
  appendedIds: readonly string[]
): Row<T>[] {
  if (appendedIds.length === 0) return sorted;
  const appended = new Set(appendedIds);
  const byId = new Map(sorted.map(row => [row.id, row]));
  return [
    ...sorted.filter(row => !appended.has(row.id)),
    ...appendedIds.flatMap(id => byId.get(id) ?? []),
  ];
}
